import React, { useState, useRef, useEffect } from 'react';
import StatsRow from './StatsRow';
import GroupRow from './GroupRow';
import CreatorTable from './CreatorTable';
import { IconPlus, IconX } from '@tabler/icons-react';

const CampaignView = ({
 campaign,
 groups,
 creators,
 onCreateGroup,
 onManageGroup,
 onGroupAssign,
 onUnassignCreator,
 onDuplicateGroup,
 onDeleteGroup,
 onAddCreator
}) => {
 const [selectedCreators, setSelectedCreators] = useState([]);
 const [activeDropdownCreatorId, setActiveDropdownCreatorId] = useState(null);
 const [showNewGroupForm, setShowNewGroupForm] = useState(false);
 const [newGroupName, setNewGroupName] = useState('');
 const [newGroupSize, setNewGroupSize] = useState(10);
 const newGroupNameRef = useRef(null);

 useEffect(() => {
 if (showNewGroupForm && newGroupNameRef.current) {
 newGroupNameRef.current.focus();
 }
 }, [showNewGroupForm]);

 const handleOpenNewGroup = () => {
 setNewGroupName(`Group ${groups.length + 1}`);
 setNewGroupSize(10);
 setShowNewGroupForm(true);
 };

 const handleCreateGroup = () => {
 const size = Math.max(1, Math.min(100, Number(newGroupSize) || 10));
 onCreateGroup({ name: newGroupName.trim() || undefined, maxSize: size });
 setShowNewGroupForm(false);
 };

 const handleGroupClick = (creatorId) => {
 setActiveDropdownCreatorId(activeDropdownCreatorId === creatorId ? null : creatorId);
 };

 const handleAssign = (creatorId, groupId) => {
 // If multiple creators selected, assign all selected; otherwise just the one
 const ids = selectedCreators.length > 1 && selectedCreators.includes(creatorId)
 ? selectedCreators
 : [creatorId];
 onGroupAssign(ids, groupId);
 setActiveDropdownCreatorId(null);
 if (ids.length > 1) setSelectedCreators([]);
 };

 const handleUnassign = (creatorId, groupId) => {
 if (onUnassignCreator) {
 onUnassignCreator(creatorId, groupId);
 }
 setActiveDropdownCreatorId(null);
 };

 const totalNeeded = campaign?.requiredCreators || 0;
 const totalAssigned = creators.filter(c => c.groupId !== null).length;
 const groupsNeeded = Math.ceil(totalNeeded / (groups[0]?.groupSize || 5));
 const moreGroupsNeeded = groupsNeeded - groups.length;

 return (
 <div>
 <StatsRow campaign={campaign} creators={creators} />

 {/* Groups Overview */}
 <div className="bg-surface-1 border border-glass-border p-3 sm:p-5 mb-6">
 <div className="flex justify-between items-center mb-4 gap-3">
 <h2 className="text-lg font-semibold text-text-primary">
 Groups ({groups.length})
 </h2>
 <button
 onClick={handleOpenNewGroup}
 className="px-4 py-2 text-sm font-medium bg-accent hover:bg-accent-hover text-white flex items-center gap-2 flex-shrink-0"
 >
 <IconPlus size={16} />
 New Group
 </button>
 </div>

 {showNewGroupForm && (
 <div className="mb-4 p-4 bg-surface-0 border border-glass-border">
 <div className="flex items-center justify-between mb-3">
 <h3 className="text-sm font-semibold text-text-primary">New Group</h3>
 <button onClick={() => setShowNewGroupForm(false)} className="text-text-tertiary hover:text-text-primary">
 <IconX size={16} />
 </button>
 </div>
 <div className="flex flex-wrap items-end gap-3">
 <div className="flex-1 min-w-[120px]">
 <label className="block text-xs font-medium text-text-secondary mb-1">Group Name</label>
 <input
 ref={newGroupNameRef}
 type="text"
 value={newGroupName}
 onChange={(e) => setNewGroupName(e.target.value)}
 onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
 className="w-full px-3 py-2 text-sm border border-glass-border bg-surface-1 text-text-primary focus:ring-2 focus:ring-accent focus:border-transparent"
 placeholder="Group name"
 />
 </div>
 <div className="w-20 sm:w-24">
 <label className="block text-xs font-medium text-text-secondary mb-1">Size</label>
 <input
 type="number"
 min={1}
 max={100}
 value={newGroupSize}
 onChange={(e) => setNewGroupSize(e.target.value)}
 onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
 className="w-full px-3 py-2 text-sm border border-glass-border bg-surface-1 text-text-primary focus:ring-2 focus:ring-accent focus:border-transparent"
 />
 </div>
 <button
 onClick={handleCreateGroup}
 className="px-4 py-2 text-sm font-medium bg-accent hover:bg-accent-hover text-white w-full sm:w-auto"
 >
 Create
 </button>
 </div>
 </div>
 )}

 {groups.map(group => (
 <GroupRow
 key={group._id}
 group={group}
 creators={creators}
 onManage={onManageGroup}
 onDuplicate={onDuplicateGroup}
 onDelete={onDeleteGroup}
 />
 ))}

 {groups.length === 0 && (
 <div className="text-center py-8 text-text-secondary">
 No groups yet. Click "New Group" to get started.
 </div>
 )}

 {moreGroupsNeeded > 0 && groups.length > 0 && (
 <div className="text-center py-3 text-sm text-text-secondary">
 {moreGroupsNeeded} more group{moreGroupsNeeded !== 1 ? 's' : ''} needed to fill {totalNeeded} creators ({totalAssigned}/{totalNeeded} assigned)
 </div>
 )}
 </div>

 {/* Creator Table with inline dropdown support */}
 <CreatorTable
 creators={creators}
 groups={groups}
 selectedCreators={selectedCreators}
 onSelectionChange={setSelectedCreators}
 onGroupClick={handleGroupClick}
 activeDropdownCreatorId={activeDropdownCreatorId}
 onGroupAssign={(creatorId, groupId) => handleAssign(creatorId, groupId)}
 onUnassignCreator={(creatorId, groupId) => handleUnassign(creatorId, groupId)}
 onCreateNewForCreator={(creatorId) => {
 const ids = selectedCreators.length > 1 && selectedCreators.includes(creatorId)
 ? selectedCreators
 : [creatorId];
 onGroupAssign(ids, 'new');
 setActiveDropdownCreatorId(null);
 if (ids.length > 1) setSelectedCreators([]);
 }}
 onCloseDropdown={() => setActiveDropdownCreatorId(null)}
 onAddCreator={onAddCreator}
 />
 </div>
 );
};

export default CampaignView;
